/**
 * PricingTierDetail — a rich, single-card panel for the pricing page's per-tier
 * tabs. Price, billing options, the full capacity set, the included features,
 * and a data-driven infra/API-route diagram are all read from the shared
 * Stripe-shaped pricing.json (Rule 17 — no hardcoded prices or capacity limits
 * in the marketing layer). Currency + billing-frequency toggles are live.
 *
 * The topology diagram is a themed React SVG (tokens via CSS vars, so it adapts
 * to light/dark automatically) rather than a baked asset — it stays live
 * against pricing.json. Composes Card + Button + Badge + Segmented + tokens.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Card } from '../card';
import { Button } from '../button';
import { Badge } from '../badge';
import { MarketingLink } from '../marketing-link';
import { Segmented } from './Segmented';
import rawPricing from '../../data/pricing.json';
import {
  tierToPricingTierData, tierCapacityRows,
  type RawPricingResponse,
} from '../../data/pricing-transform';
import type { Currency, BillingFrequency, PricingTierData } from '../../pricing';

const RAW = rawPricing as RawPricingResponse;

/**
 * Tier id+name list, derived from the pricing source. The pricing page maps
 * over this to build one tab per tier — add a tier in Stripe / pricing.json
 * and its tab appears automatically (Rule 17 + 18, nothing hand-listed).
 */
export const PRICING_TIER_SUMMARIES: readonly { readonly id: string; readonly name: string }[] =
  RAW.tiers.map((t) => ({ id: t.tier, name: t.name }));

const SYMBOL: Record<Currency, string> = { GBP: '£', EUR: '€', USD: '$' };
const CURRENCIES: readonly Currency[] = ['GBP', 'EUR', 'USD'];
const FREQS: readonly { v: BillingFrequency; label: string }[] = [
  { v: 'monthly', label: 'Monthly' },
  { v: 'quarterly', label: 'Quarterly −5%' },
  { v: 'annual', label: 'Annual −10%' },
];
const MONTHS: Record<BillingFrequency, number> = { monthly: 1, quarterly: 3, annual: 12 };

// Illustrative app names for the integration grid (examples, not pricing data —
// the count of tiles is what's data-driven from the tier's entitlement).
const APP_POOL = ['Salesforce', 'NetSuite', 'Zendesk', 'Snowflake', 'HubSpot', 'Stripe'] as const;

// Growth-ladder gradient ramp — 135° brand gradients deepening with each tier
// (the deck/design-system aesthetic). `dark` flags a pale step that needs dark
// text; the deeper steps carry light text. Clamped to the last entry if a
// pricing tier is ever added beyond the ramp.
const RAMP = [
  { from: 'var(--brand-100)', to: 'var(--brand-300)', dark: true },
  { from: 'var(--brand-300)', to: 'var(--brand-500)', dark: true },
  { from: 'var(--brand-500)', to: 'var(--brand-700)', dark: false },
  { from: 'var(--brand-700)', to: 'var(--brand-800)', dark: false },
] as const;

const SANS = "'Space Grotesk', ui-sans-serif, system-ui, sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace";

function fmt(amount: number, currency: Currency): string {
  return `${SYMBOL[currency]}${Math.round(amount).toLocaleString('en-GB')}`;
}

interface LadderStep { readonly id: string; readonly name: string; readonly integrations: number; readonly current: boolean; }

/**
 * Infra + API-route topology for a tier, in two panels:
 *  1. the flow — your stack → API/MCP/SDK routes → the Inferrex platform →
 *     a grid of your integrations (example apps, however many this tier allows);
 *  2. a growth ladder — active integrations across every tier on a deepening
 *     135° brand-gradient ramp (the deck aesthetic), this one lit, each step
 *     clickable to jump to that tier's tab.
 * Everything is driven by pricing.json. Token colours via CSS vars → theme-aware
 * with no dark variant. Gradient ids are namespaced by tier to stay unique.
 */
function TierTopologyDiagram({ apps, ladder, integrations, idPrefix, onSelect }: {
  apps: readonly string[]; ladder: readonly LadderStep[]; integrations: string;
  idPrefix: string; onSelect: (tierId: string) => void;
}) {
  const rows = Math.ceil(apps.length / 2);
  const gridH = rows * 30 + (rows - 1) * 8;
  const gridTop = 92 - gridH / 2;

  // ladder geometry: N equal steps across the width, arrowed left→right
  const n = ladder.length;
  const M = 16, GAP = 18;
  const stepW = (720 - M * 2 - GAP * (n - 1)) / n;

  return (
    <svg viewBox="0 0 720 274" className="w-full" role="img"
      aria-label={`How it runs: your stack connects through API, MCP and SDK routes to the Inferrex platform, which self-heals up to ${integrations} integrations. Active integrations grow with each tier.`}>
      <defs>
        <marker id="tt-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0 0 L6 3 L0 6 z" fill="var(--brand-500)" />
        </marker>
        <marker id="tt-arrow-mut" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0 0 L6 3 L0 6 z" fill="var(--brand-300)" />
        </marker>
        <linearGradient id={`${idPrefix}-plat`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--brand-500)" />
          <stop offset="1" stopColor="var(--brand-700)" />
        </linearGradient>
        {ladder.map((_s, i) => {
          const ramp = RAMP[Math.min(i, RAMP.length - 1)]!;
          return (
            <linearGradient key={i} id={`${idPrefix}-step${i}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor={ramp.from} />
              <stop offset="1" stopColor={ramp.to} />
            </linearGradient>
          );
        })}
      </defs>

      {/* ── panel 1: the flow ── */}
      <path d="M148 92 L272 92" stroke="var(--brand-500)" strokeWidth="2" fill="none" markerEnd="url(#tt-arrow)" />
      <path d="M432 92 L536 92" stroke="var(--brand-500)" strokeWidth="2" fill="none" markerEnd="url(#tt-arrow)" />

      <rect x="16" y="56" width="132" height="72" rx="10" fill="var(--card)" stroke="var(--border)" strokeWidth="1.5" />
      <text x="82" y="88" textAnchor="middle" fontFamily={SANS} fontSize="14" fontWeight="600" fill="var(--card-foreground)">Your stack</text>
      <text x="82" y="108" textAnchor="middle" fontFamily={SANS} fontSize="12" fill="var(--muted-foreground)">apps · DBs · files</text>

      <rect x="276" y="48" width="156" height="88" rx="12" fill={`url(#${idPrefix}-plat)`} />
      <text x="354" y="86" textAnchor="middle" fontFamily={SANS} fontSize="15" fontWeight="700" fill="var(--brand-50)">Inferrex platform</text>
      <text x="354" y="108" textAnchor="middle" fontFamily={MONO} fontSize="11" fill="var(--brand-100)">infer · sync · heal</text>

      {/* integration grid — one tile per example app, scaled to the tier */}
      {apps.map((app, i) => {
        const c = i % 2, r = Math.floor(i / 2);
        const x = c === 0 ? 540 : 628;
        const y = gridTop + r * 38;
        const more = app.startsWith('+');
        return (
          <g key={i}>
            <rect x={x} y={y} width="76" height="30" rx="7"
              fill={more ? 'var(--brand-50)' : 'var(--card)'} stroke={more ? 'var(--brand-100)' : 'var(--border)'} strokeWidth="1.5" />
            <text x={x + 38} y={y + 19} textAnchor="middle" fontFamily={SANS} fontSize="9.5"
              fontWeight={more ? 600 : 500} fill={more ? 'var(--brand-700)' : 'var(--card-foreground)'}>{app}</text>
          </g>
        );
      })}

      <text x="210" y="82" textAnchor="middle" fontFamily={MONO} fontSize="10.5" fill="var(--muted-foreground)">API · MCP · SDK</text>
      <text x="484" y="82" textAnchor="middle" fontFamily={MONO} fontSize="10.5" fill="var(--muted-foreground)">sync · heal</text>

      {/* ── panel 2: the growth ladder (gradient ramp, clickable) ── */}
      <text x="16" y="178" fontFamily={MONO} fontSize="10" letterSpacing="0.5" fill="var(--muted-foreground)">ACTIVE INTEGRATIONS BY TIER — TAP TO COMPARE</text>
      {ladder.map((s, i) => {
        const x = M + i * (stepW + GAP);
        const ramp = RAMP[Math.min(i, RAMP.length - 1)]!;
        const nameFill = ramp.dark ? 'var(--brand-800)' : 'var(--brand-50)';
        const subFill = ramp.dark ? 'var(--brand-700)' : 'var(--brand-100)';
        const ring = s.current ? (ramp.dark ? 'var(--brand-600)' : 'var(--brand-300)') : 'var(--border)';
        return (
          <g key={s.id} className="cursor-pointer transition-opacity hover:opacity-85"
            onClick={() => onSelect(s.id)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(s.id); } }}>
            <title>{`View the ${s.name} tier — ${s.integrations} integrations`}</title>
            {i > 0 && (
              <path d={`M${x - GAP + 3} 218 L${x - 3} 218`} stroke="var(--brand-300)" strokeWidth="2" fill="none" markerEnd="url(#tt-arrow-mut)" />
            )}
            <rect x={x} y="190" width={stepW} height="56" rx="10"
              fill={`url(#${idPrefix}-step${i})`} stroke={ring} strokeWidth={s.current ? 2.5 : 1} />
            <text x={x + stepW / 2} y="214" textAnchor="middle" fontFamily={SANS} fontSize="13" fontWeight="600" fill={nameFill}>{s.name}</text>
            <text x={x + stepW / 2} y="232" textAnchor="middle" fontFamily={SANS} fontSize="11" fill={subFill}>{s.integrations} integrations</text>
            {s.current && (
              <text x={x + stepW / 2} y="264" textAnchor="middle" fontFamily={MONO} fontSize="9" letterSpacing="0.5" fill="var(--brand-600)">YOUR PLAN</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export interface PricingTierDetailProps {
  /** Tier id, as in pricing.json (e.g. 'pilot', 'growth'). */
  readonly tier: string;
  /** Deep-link for custom-priced tiers (price === null). */
  readonly contactHref?: string;
}

export function PricingTierDetail({ tier, contactHref = '/contact' }: PricingTierDetailProps) {
  const [currency, setCurrency] = useState<Currency>('GBP');
  const [frequency, setFrequency] = useState<BillingFrequency>('monthly');
  const [, setSearchParams] = useSearchParams();

  // Ladder steps deep-link to their tier's tab. The tab slug equals the tier id
  // (MarketingTabs slugifies the single-word tier name to the same value).
  const goTier = (tierId: string) =>
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('tab', tierId); return next; });

  const raw = RAW.tiers.find((t) => t.tier === tier);
  if (!raw) return null;
  const data: PricingTierData = tierToPricingTierData(raw);
  const capacity = tierCapacityRows(raw);

  // Example apps for the integration grid, scaled to this tier's allowance:
  // show named tiles up to the pool, then a "+N more" tile once it exceeds it.
  const intCount = raw.entitlements?.integrations ?? 0;
  const apps = intCount <= APP_POOL.length
    ? APP_POOL.slice(0, intCount)
    : [...APP_POOL.slice(0, APP_POOL.length - 1), `+${intCount - (APP_POOL.length - 1)} more`];
  // Growth ladder across the self-serve tiers (sales-engaged tiers have no fixed
  // entitlements), this one lit.
  const ladder = RAW.tiers
    .filter((t) => t.entitlements)
    .map((t) => ({ id: t.tier, name: t.name, integrations: t.entitlements!.integrations, current: t.tier === raw.tier }));

  const total = data.prices[currency][frequency];
  const perMonth = total === null ? null : total / MONTHS[frequency];

  return (
    <Card className={`my-8 overflow-hidden p-0 ${data.recommended ? 'border-primary ring-1 ring-primary' : ''}`}>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px]">
        {/* ── left: the tier, in depth ── */}
        <div className="space-y-7 p-6 lg:p-8">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="font-display text-2xl font-bold text-foreground">{raw.name}</h2>
              {data.recommended && <Badge>Popular</Badge>}
            </div>
            {raw.description && <p className="mt-2 text-muted-foreground">{raw.description}</p>}
          </div>

          {/* infra / API-route topology, annotated with this tier's capacity */}
          <figure className="rounded-card border border-border bg-muted/30 p-4">
            <TierTopologyDiagram apps={apps} ladder={ladder} integrations={String(intCount)} idPrefix={tier} onSelect={goTier} />
            <figcaption className="mt-2 text-center text-xs text-muted-foreground">
              One platform, one API surface — {raw.name} scales the capacity, not the architecture.
            </figcaption>
          </figure>

          {/* full capacity set */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Capacity</h3>
            <div className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-card border border-border bg-border sm:grid-cols-4">
              {capacity.map((c) => (
                <div key={c.label} className="bg-card p-3">
                  <div className="text-xs text-muted-foreground">{c.label}</div>
                  <div className="mt-1 font-display text-lg font-semibold text-foreground">{c.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* everything included */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Everything included</h3>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {data.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
                  <span>{f.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── right rail: price + CTA ── */}
        <div className="flex flex-col border-t border-border bg-muted/30 p-6 lg:border-l lg:border-t-0 lg:p-8">
          <div className="flex flex-col gap-2">
            <Segmented value={currency} onChange={setCurrency} options={CURRENCIES.map((c) => ({ v: c, label: c }))} />
            <Segmented value={frequency} onChange={setFrequency} options={FREQS} className="flex-wrap" />
          </div>

          <div className="mt-5">
            {perMonth === null ? (
              <div className="font-display text-4xl font-bold text-foreground">Custom</div>
            ) : (
              <>
                <span className="font-display text-4xl font-bold text-primary">{fmt(perMonth, currency)}</span>
                <span className="text-sm text-muted-foreground"> /mo</span>
                {frequency !== 'monthly' && total !== null && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {fmt(total, currency)} billed {frequency === 'annual' ? 'annually' : 'quarterly'}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="pt-6">
            <Button asChild className="w-full" variant={data.recommended ? 'default' : 'outline'}>
              <MarketingLink href={perMonth === null ? contactHref : data.ctaHref}>
                {perMonth === null ? 'Talk to us' : data.ctaLabel}
              </MarketingLink>
            </Button>
            <p className="mt-3 text-center text-xs text-muted-foreground">Cancel within 30 days · unlimited users</p>
          </div>

          <div className="mt-auto pt-6 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Need more capacity?</p>
            <p className="mt-1">
              Add a capacity pack or enable opt-in overage from your dashboard — or{' '}
              <MarketingLink href="/contact" className="text-primary underline-offset-2 hover:underline">talk to us</MarketingLink>{' '}
              about Enterprise &amp; Sovereign for unlimited scale.
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
